import { useCallback, useEffect, useState } from 'react';
import { systemParameters, type ScoringSettings } from '../api/systemParameters';
import { IconRefreshCw } from '../icons';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Alert, Field, Select, TextInput, Toggle } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';

/**
 * Sistem Parametreleri — the settings that decide how the product behaves rather than what
 * is in it.
 *
 * <p>A page of sections rather than a table of keys and values. The table was the obvious
 * shape and the wrong one: nothing typed, nothing validated, every reader parsing a string,
 * and the day somebody writes the wrong spelling of a value the scores change with nothing
 * refusing it. Each section here is its own endpoint and its own shape, and the page grows
 * by gaining another one.</p>
 */
export function SystemParameters() {
  const t = useTranslate();

  const [scoring, setScoring] = useState<ScoringSettings | null>(null);
  const [dirty, setDirty] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setScoring(await systemParameters.scoring());
      setError(null);
      setDirty(false);
    } catch (e) {
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const change = (next: Partial<ScoringSettings>) => {
    setScoring((was) => (was ? { ...was, ...next } : was));
    setDirty(true);
    setSaved(false);
  };

  const save = async () => {
    if (!scoring) return;

    setSaving(true);
    try {
      setScoring(await systemParameters.saveScoring(scoring));
      setError(null);
      setDirty(false);
      setSaved(true);
    } catch (e) {
      setError(describeError(e, 'error.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        crumbs={[t('app.definitions')]}
        title={t('systemParameters.title')}
        subtitle={t('systemParameters.subtitle')}
        action={
          <Button onClick={() => void save()} disabled={!dirty || saving || loading}>
            {t('common.save')}
          </Button>
        }
        aside={saved ? <Badge tone="comment">{t('systemParameters.saved')}</Badge> : null}
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card className="nv-pad">
        <h2 className="nv-section__title">{t('systemParameters.scoring')}</h2>
        <p className="nv-hint">{t('systemParameters.scoringHint')}</p>

        {loading || !scoring ? (
          <p className="nv-muted">
            <IconRefreshCw className="nv-spin" /> {t('common.loading')}
          </p>
        ) : (
          <>
            <Field label={t('systemParameters.model')} hint={t('systemParameters.modelHint')}>
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={scoring.model}
                  onChange={(e) => change({ model: e.target.value as ScoringSettings['model'] })}
                >
                  <option value="Weight">{t('scoringModel.Weight')}</option>
                  <option value="Points">{t('scoringModel.Points')}</option>
                </Select>
              )}
            </Field>

            {/*
             * Shown under both models. The target is kept while the tenant is on weights so
             * that switching back and forth does not lose a number somebody chose, and a
             * field that disappears is a field people believe they have to retype.
             */}
            <Field label={t('systemParameters.target')} hint={t('systemParameters.targetHint')}>
              {({ id, describedBy }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  type="number"
                  min={1}
                  step={1}
                  value={scoring.pointsTarget}
                  disabled={scoring.model !== 'Points'}
                  onChange={(e) => change({ pointsTarget: Number(e.target.value) })}
                />
              )}
            </Field>

            {/*
             * Said plainly, because the opposite is what everybody assumes a settings page
             * does. Nothing above here reaches backwards: a template carries its own model
             * from the day it was created, and a score already given never moves.
             */}
            <Alert tone="info">{t('systemParameters.onlyNewTemplates')}</Alert>

            {/*
             * The two below are the opposite, and the note says so. They decide what the
             * audit screen draws rather than what a number means, so they take effect on
             * the visit somebody is in the middle of — which is what anyone turning one on
             * is expecting.
             */}
            <div className="nv-drawer__section">
              <Toggle
                checked={scoring.showQuestionPoints}
                onCheckedChange={(showQuestionPoints) => change({ showQuestionPoints })}
                label={t('systemParameters.showQuestionPoints')}
              />
              <p className="nv-hint">{t('systemParameters.showQuestionPointsHint')}</p>
            </div>

            <div className="nv-drawer__section">
              <Toggle
                checked={scoring.showRunningScore}
                onCheckedChange={(showRunningScore) => change({ showRunningScore })}
                label={t('systemParameters.showRunningScore')}
              />
              <p className="nv-hint">{t('systemParameters.showRunningScoreHint')}</p>
            </div>

            <Alert tone="info">{t('systemParameters.displayTakesEffectNow')}</Alert>
          </>
        )}
      </Card>
    </>
  );
}
